import React, { createRef, useContext, useEffect, useState } from 'react'
import { AppContext } from "../context/AppContext";
import {
  getUser,
  createUser,
  updateUser,
  updateBillingDetails,
  deleteUser,
  getSuggestions,
  logoutUser,
  getDepartments,
} from "../services/adminServices";
import SimpleReactValidator from "simple-react-validator";
import { Cropper } from 'react-cropper';
import { CONFIG } from '../config/general';
import { toast } from 'react-toastify';
import DropdownWithCheckbox from './Selection';
import { Link } from 'react-router-dom';

const Users = () => {
  const { login } = useContext(AppContext);
  const [validatorCount, setValidatorCount] = useState(0);
  const forceUpdate = () => setValidatorCount(validatorCount + 1);
  const initial = new SimpleReactValidator({
    locale: "en", autoForceUpdate: { forceUpdate }, validators: {
      passwordValidation: {
        message: "Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character.",
        rule: (val, params, validator) => {
          const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
          return regex.test(val);
        },
        required: true,
      },
    },
  });
  const [validator, setValidator] = useState(initial);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filter, setFilter] = useState({ name: "", limit: 50, page: 1 });
  const [pagination, setPagination] = useState({});
  const [editMember, setEditMember] = useState(null);
  const [suggestion, setSuggestion] = useState([]);
  const [newMember, setNewMember] = useState({
    gender: "male",
    name: "",
    designation: "",
    departments: [],
    username: "",
    userEmail: "",
    userPassword: "",
    profileImage: '',
  });
  const cropperRef = createRef();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBilling, setIsBilling] = useState({
    _id: null,
    selection: false,
    includeArray: [],
    includeValue: "",
    notIncludeValue: "",
    notIncludeArray: []
  });


  const getAllUsers = async (filters = filter) => {
    try {
      let result = await getUser(login.token, filters);
      setPagination(result.pagination)
      setUsers(result.data.map((x) => ({ ...x, isGroup: false })));
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const getAllDepartments = async () => {
    try {
      let result = await getDepartments(login.token, {});
      setDepartments(result.data)
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const getAllSuggestions = async () => {
    try {
      let result = await getSuggestions(login.token);
      setSuggestion(result.suggestions)
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleCreateMember = async () => {
    try {
      if (validator.allValid()) {
        const formData = new FormData();
        const cropper = cropperRef.current?.cropper;
        let memberDetails = newMember
        delete memberDetails.profileImage
        if (cropper) {
          cropper.getCroppedCanvas().toBlob(async (blob) => {
            const croppedFile = new File([blob], "profile-pic.png", { type: "image/png" });
            Object.entries(memberDetails).forEach(([key, value]) => {
              formData.append(key, value);
            });
            formData.append(`profilePicture`, croppedFile);
            const result = await createUser(login.token, formData);
            if (result.success) {
              toast.success(result.message)
            } else {
              toast.error(result.message)
            }
            setNewMember({
              gender: "male",
              name: "",
              username: "",
              userEmail: "",
              userPassword: "",
            });
            getAllUsers();
            setIsModalOpen(false); // Close the modal after success
          })
        }
      } else {
        validator.showMessages();
        forceUpdate();
      }
    } catch (error) {
      console.error("Error creating member:", error);
      toast.error("Something went wrong")
    }
  };

  const handleUpdateMember = async () => {
    try {
      if (validator.allValid()) {
        const formData = new FormData();
        const cropper = cropperRef.current?.cropper;
        let memberDetails = { ...editMember };
        delete memberDetails.profileImage;
        let isPassword =
          editMember.userPassword ==
          users.find((x) => x._id == editMember._id).userPassword;
        if (isPassword) {
          delete memberDetails.userPassword;
        }
        Object.entries(memberDetails).forEach(([key, value]) => {
          formData.append(key, JSON.stringify(value));
        });

        if (editMember.isImgUpdated && cropper) {
          // Process and upload the new profile picture
          cropper.getCroppedCanvas().toBlob(async (blob) => {
            if (blob) {
              const croppedFile = new File([blob], "profile-pic.png", { type: "image/png" });
              formData.append("updatedPic", croppedFile);
            }
            // Send updated data to the server
            const result = await updateUser(login.token, editMember._id, formData);
            if (result.success) {
              toast.success(result.message)
            } else {
              toast.error(result.message)
            }
            setEditMember(null);
            getAllUsers();
          });
        } else {
          const result = await updateUser(login.token, editMember._id, formData);
          if (result.success) {
            toast.success(result.message)
          } else {
            toast.error(result.message)
          }
          setEditMember(null);
          getAllUsers();
        }
      } else {
        validator.showMessages();
        forceUpdate();
      }
    } catch (error) {
      console.error("Error updating member:", error);
      toast.error("Something went wrong")
    }
  };

  const handleFileChange = (e, isEdit) => {
    let files;
    if (e.dataTransfer) {
      files = e.dataTransfer.files;
    } else if (e.target) {
      files = e.target.files;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (isEdit) {
        setEditMember({ ...editMember, isImgUpdated: true, profileImage: reader.result })
      } else {
        setNewMember({ ...newMember, profileImage: reader.result })
      }
    };
    reader.readAsDataURL(files[0]);
  };
  const onCrop = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      const cropBoxData = cropper.getCropBoxData();
      const cropWidth = cropBoxData.width;
      const cropHeight = cropBoxData.height;
      if (cropWidth < 100 || cropHeight < 100) {
        let newCropWidth = Math.max(cropWidth, 100);
        let newCropHeight = Math.max(cropHeight, 100);
        cropper.setCropBoxData({ width: newCropWidth, height: newCropHeight });
      }
    }
  };

  const handleNext = () => {
    if (pagination.currentPage < pagination.totalPages) {
      const newPage = pagination.currentPage + 1;
      setFilter({ ...filter, page: newPage });
      getAllUsers({ ...filter, page: newPage });
    }
  };

  const handlePrev = () => {
    if (pagination.currentPage > 1) {
      const newPage = pagination.currentPage - 1;
      setFilter({ ...filter, page: newPage });
      getAllUsers({ ...filter, page: newPage });
    }
  };

  const handleUpdateBilling = async () => {
    if (!isBilling?._id) {
      toast.error("Something went wrong")
      return;
    }
    const payload = {
      includeArray: isBilling.includeArray || [],
      notIncludeArray: isBilling.notIncludeArray || [],
    };
    let result = await updateBillingDetails(login.token, isBilling._id, payload);
    if (result.success) {
      toast.success(result.message)
    } else {
      toast.error(result.message)
    }
    getAllUsers()
    setIsBilling({
      _id: null,
      includeArray: [],
      includeValue: "",
      notIncludeValue: "",
      notIncludeArray: []
    })
  }

  const handleDeleteUser = async (id) => {
    try {
      let result = await deleteUser(login.token, id)
      if (result.success) {
        toast.success(result.message)
      } else {
        toast.error(result.message)
      }
      getAllUsers()
    } catch (error) {
      console.log(error)
    }
  }

  const handleLogout = async (id) => {
    try {
      let result = await logoutUser(login.token, id)
      if (result.success) {
        toast.success(result.message)
      } else {
        toast.error(result.message)
      }
      getAllUsers()
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    setUsers([]);
    getAllUsers();
    getAllDepartments();
    getAllSuggestions()
  }, []);

  useEffect(() => {
    getAllUsers();
  }, [filter.limit]);

  validator.purgeFields();

  return (
    <div className="details-table mt-3">
      <div className="search-container">
        <h4>
          <i class="fas fa-user"></i> Users Details :
        </h4>
        <div className="headerWrap gap-3">
          <form onSubmit={(e) => {
            e.preventDefault();
            getAllUsers();
          }} className="d-flex">
            <button type="submit" id="searchButton" style={{ background: "none", border: "none", padding: 0 }}>
              <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.5 21.5C16.7467 21.5 21 17.2467 21 12C21 6.75329 16.7467 2.5 11.5 2.5C6.25329 2.5 2 6.75329 2 12C2 17.2467 6.25329 21.5 11.5 21.5Z" stroke="#657488" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                <path d="M22 22.5L20 20.5" stroke="#657488" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
              </svg>
            </button>
            <input
              className="fs-18 tableSearch"
              type="text"
              value={filter.name}
              onChange={(e) => setFilter({ ...filter, name: e.target.value })}
              id="searchInput"
              name="search"
              placeholder="Search here . . ."
            />
          </form>
          <div className="add-user py-3 text-end">
            <h4>
              <a onClick={() => setIsModalOpen(true)}>
                <i className="fa-solid me-3 fa-user-plus"></i>
              </a>
            </h4>
          </div>
        </div>
      </div>
      <div className="custom-pagination">
        <div className="d-flex gap-2 align-items-center">
          <span className="showing-entries">Showing {(pagination.currentPage - 1) * filter.limit + 1} - {Math.min((pagination.currentPage) * filter.limit, pagination.totalUsers)} of {pagination.totalUsers}</span>
          <div>
            <div className="dropdown">
              <button
                className="btn btn-sm btn-outline-secondary dropdown-toggle w-100"
                type="button"
                id="limitDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {filter.limit || "Select Limit"}
              </button>

              <ul className="dropdown-menu w-100" aria-labelledby="limitDropdown">
                {[50, 100, 150, 200].map((val) => (
                  <li key={val}>
                    <button
                      className="dropdown-item"
                      type="button"
                      onClick={() => setFilter({ ...filter, limit: val })}
                    >
                      {val}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

        </div>
        <div className="page-controls">
          <button
            className="page-btn prev"
            onClick={handlePrev}
            disabled={pagination.currentPage === 1} // Disable button on first page
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          <span className="current-page">{pagination.currentPage}</span>
          <span className="separator">/</span>
          <span className="total-pages">{pagination.totalPages}</span>
          <button
            className="page-btn next"
            onClick={handleNext}
            disabled={pagination.currentPage === pagination.totalPages} // Disable button on last page
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
      <table id="example" className="display" style={{ "width": "100%" }}>
        <thead>
          <tr>
            <th>No</th>
            <th>Name</th>
            <th>Departments</th>
            <th>Designation</th>
            <th style={{ width: "170px" }}>Mobile</th>
            <th style={{ width: "140px" }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {
            users.map((x, i) => {
              return (
                <tr key={x._id}>
                  <td>{((pagination.currentPage - 1) * filter.limit) + (i + 1)}</td>
                  <td>
                    <div className='supportNumber d-flex align-items-center position-relative'>
                      <p
                        id="supportText"
                        className="flex-grow-1 text-break position-relative"
                        style={{ marginRight: "10px", width: "90%" }}
                      >
                        {x.name}
                        <button
                          onClick={() => {
                            navigator.clipboard
                              .writeText(x._id)
                              .then(() => toast.success("Copied to clipboard!"))
                              .catch((err) => toast.error("Failed to copy text: " + err));
                          }}
                          className="btn btn-warning btn-sm position-absolute top-50 end-0 translate-middle-y"
                          style={{ display: "none", backgroundColor: "#8ec73c" }}
                        >
                          Copy
                        </button>
                      </p>
                    </div>
                  </td>

                  <td>
                    {x.departments
                      ?.map(dep => departments.find(d => d._id == dep)?.name)
                      .filter(Boolean) // Remove undefined values
                      .join(", ") || "N/A"}
                  </td>

                  <td>{x.designation}</td>
                  <td>{x.userPhone}</td>
                  <td>
                    <div className="export-chat edit-delete-user">
                      <Link to={`/viewUser/` + x._id}>
                        <i className="fa-regular fa-pen-to-square"></i>Edit
                      </Link>
                      {/* <a onClick={() => setEditMember(x)}>
                        <i className="fa-regular fa-pen-to-square"></i>Edit
                      </a>
                      <a onClick={() => handleDeleteUser(x._id)}>
                        <i className="fa-regular fa-trash-can"></i>{x.isDeleted ? "Enable" : "Disable"}
                      </a>
                      <a onClick={() => setIsBilling({
                        ...isBilling,
                        selection: x.billingDetails?.notIncludeArray.length > 0 ? false : true,
                        notIncludeArray: x.billingDetails?.notIncludeArray || [],
                        includeArray: x.billingDetails?.includeArray || [],
                        _id: x._id
                      })}>
                        <i className="fa-regular fa-pen-to-square"></i>Edit Work Details
                      </a>
                      <a onClick={() => handleLogout(x._id)}>
                        <i className="fa-regular fa-trash-can"></i>Logout</a> */}
                    </div>
                  </td>
                </tr>
              )
            })
          }
        </tbody>
      </table>
      {isModalOpen && (
        <div className="modal show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-lg" role="document"> {/* Larger modal for two columns */}
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create New Member</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setIsModalOpen(false)}
                ></button>
              </div>

              <div className="modal-body">
                <form>
                  <div className="row">
                    {/* LEFT SIDE → Member Info */}
                    <div className="col-md-7">
                      <div className="row g-3">
                        <div className="col-lg-6">
                          <label htmlFor="name" className="form-label">Name</label>
                          <input
                            type="text"
                            className="form-control"
                            id="name"
                            placeholder="Enter your name"
                            value={newMember.name}
                            onChange={(e) =>
                              setNewMember({ ...newMember, name: e.target.value })
                            }
                          />
                          <span className="text-danger small">
                            {validator.message(
                              "Name",
                              newMember.name,
                              "required|max:50",
                              { max: "Name cannot exceed 50 characters." }
                            )}
                          </span>
                        </div>

                        <div className="col-lg-6">
                          <label htmlFor="designation" className="form-label">Designation</label>
                          <input
                            type="text"
                            className="form-control"
                            id="designation"
                            placeholder="Enter your designation"
                            value={newMember.designation}
                            onChange={(e) =>
                              setNewMember({
                                ...newMember,
                                designation: e.target.value,
                              })
                            }
                          />
                          <span className="text-danger small">
                            {validator.message(
                              "Designation",
                              newMember.designation,
                              "required|max:50",
                              { max: "Designation cannot exceed 50 characters." }
                            )}
                          </span>
                        </div>

                        <div className="col-lg-6">
                          <label htmlFor="mobile" className="form-label">Mobile Number</label>
                          <input
                            type="number"
                            className="form-control"
                            id="mobile"
                            placeholder="Enter your mobile number"
                            value={newMember.userPhone}
                            onChange={(e) => {
                              if (
                                e.target.value.length > 10 ||
                                isNaN(e.target.value)
                              ) {
                                return;
                              }
                              setNewMember({
                                ...newMember,
                                userPhone: e.target.value,
                              });
                            }}
                          />
                          <span className="text-danger small">
                            {validator.message(
                              "Phone Number",
                              newMember.userPhone,
                              "required|regex:^\\d{10}$",
                              { regex: "Enter a valid 10-digit phone number." }
                            )}
                          </span>
                        </div>

                        <div className="col-lg-6">
                          <label htmlFor="username" className="form-label">Username</label>
                          <input
                            type="text"
                            className="form-control"
                            id="username"
                            placeholder="Enter your username"
                            value={newMember.username}
                            onChange={(e) =>
                              setNewMember({
                                ...newMember,
                                username: e.target.value,
                              })
                            }
                          />
                          <span className="text-danger small">
                            {validator.message(
                              "username",
                              newMember.username,
                              "required|max:50",
                              { max: "username cannot exceed 50 characters." }
                            )}
                          </span>
                        </div>

                        <div className="col-lg-6">
                          <label htmlFor="email" className="form-label">Email</label>
                          <input
                            type="email"
                            className="form-control"
                            id="email"
                            placeholder="Enter your email"
                            value={newMember.userEmail}
                            onChange={(e) =>
                              setNewMember({
                                ...newMember,
                                userEmail: e.target.value,
                              })
                            }
                          />
                          <span className="text-danger small">
                            {validator.message(
                              "userEmail",
                              newMember.userEmail,
                              "required|email|max:50",
                              { max: "email cannot exceed 50 characters." }
                            )}
                          </span>
                        </div>

                        <div className="col-lg-6 mb-3">
                          <label htmlFor="gender" className="form-label">
                            Gender
                          </label>
                          <div className="dropdown">
                            <button
                              className="btn btn-sm btn-outline-secondary dropdown-toggle w-100"
                              type="button"
                              id="genderDropdown"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              {newMember.gender
                                ? newMember.gender.charAt(0).toUpperCase() +
                                newMember.gender.slice(1)
                                : "Select Gender"}
                            </button>

                            <ul
                              className="dropdown-menu w-100"
                              aria-labelledby="genderDropdown"
                            >
                              <li>
                                <button
                                  className="dropdown-item"
                                  type="button"
                                  onClick={() =>
                                    setNewMember({ ...newMember, gender: "" })
                                  }
                                >
                                  Select Gender
                                </button>
                              </li>
                              <li>
                                <button
                                  className="dropdown-item"
                                  type="button"
                                  onClick={() =>
                                    setNewMember({ ...newMember, gender: "male" })
                                  }
                                >
                                  Male
                                </button>
                              </li>
                              <li>
                                <button
                                  className="dropdown-item"
                                  type="button"
                                  onClick={() =>
                                    setNewMember({ ...newMember, gender: "female" })
                                  }
                                >
                                  Female
                                </button>
                              </li>
                            </ul>
                          </div>
                          <span className="text-danger small">
                            {validator.message("gender", newMember.gender, "required")}
                          </span>
                        </div>

                        <div className="col-lg-6" id="passwordField">
                          <label htmlFor="password" className="form-label">Password</label>
                          <input
                            type="password"
                            className="form-control"
                            id="password"
                            placeholder="Enter new password"
                            value={newMember.userPassword}
                            onChange={(e) =>
                              setNewMember({
                                ...newMember,
                                userPassword: e.target.value,
                              })
                            }
                          />
                          <span className="text-danger small">
                            {validator.message(
                              "password",
                              newMember.userPassword,
                              "required|passwordValidation"
                            )}
                          </span>
                        </div>

                        <div className="col-lg-6">
                          <label className="form-label">Select Departments</label>
                          <DropdownWithCheckbox
                            options={departments}
                            selectedItems={newMember.departments}
                            setSelectedItems={(data) => {
                              setNewMember({ ...newMember, departments: data });
                            }}
                          />
                          <span className="text-danger small">
                            {validator.message(
                              "departments",
                              newMember.departments,
                              "required"
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* RIGHT SIDE → Profile Image */}
                    <div className="col-md-5">
                      <div className="mb-3">
                        <label htmlFor="profileImage" className="form-label">
                          Profile Image
                        </label>
                        <input
                          type="file"
                          className="form-control"
                          id="profileImage"
                          accept="image/*"
                          onChange={(e) => {
                            handleFileChange(e, false);
                          }}
                        />
                        <span className="text-danger small">
                          {validator.message(
                            "Profile Image",
                            newMember.profileImage,
                            "required"
                          )}
                        </span>

                        {newMember?.profileImage && (
                          <div style={{ width: "100%", height: "300px" }}>
                            <Cropper
                              ref={cropperRef}
                              src={newMember.profileImage}
                              aspectRatio={1}
                              style={{ width: "100%", height: "100%" }}
                              initialAspectRatio={1}
                              viewMode={1}
                              crop={onCrop}
                              minCropBoxWidth={100}
                              minCropBoxHeight={100}
                              background={false}
                              responsive={true}
                              autoCropArea={1}
                              checkOrientation={false}
                              guides={true}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ backgroundColor: "#1edd60", border: "none" }}
                  onClick={handleCreateMember}
                >
                  Create Member
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {
        isBilling?._id && (
          <div className="modal show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Work Details</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setIsBilling({
                      _id: null,
                      includeArray: [],
                      includeValue: "",
                      notIncludeValue: "",
                      notIncludeArray: []
                    })}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Selection</label>
                    <div className="dropdown">
                      <button
                        className="btn btn-sm btn-outline-secondary dropdown-toggle w-100"
                        type="button"
                        id="billingSelectionDropdown"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        {isBilling.selection ? "What to Include" : "What not to Include"}
                      </button>

                      <ul className="dropdown-menu w-100" aria-labelledby="billingSelectionDropdown">
                        <li>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() =>
                              setIsBilling((prev) => ({
                                ...prev,
                                selection: false,
                                includeValue: "",
                                notIncludeValue: "",
                                includeArray: [],
                                notIncludeArray: [],
                              }))
                            }
                          >
                            What not to Include
                          </button>
                        </li>
                        <li>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() =>
                              setIsBilling((prev) => ({
                                ...prev,
                                selection: true,
                                includeArray: [],
                                notIncludeArray: [],
                                includeValue: "",
                                notIncludeValue: "",
                              }))
                            }
                          >
                            What to Include
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {isBilling.selection && <div className="mb-3">
                    <label className="form-label">What to Include</label>
                    <div className="d-flex align-items-center">
                      <input
                        type="text"
                        value={isBilling.includeValue || ""}
                        onChange={(e) =>
                          setIsBilling((prev) => ({
                            ...prev,
                            includeValue: e.target.value,
                          }))
                        }
                        className="form-control me-2"
                        placeholder="Enter a string"
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          if (isBilling.includeValue?.trim()) {
                            setIsBilling((prev) => ({
                              ...prev,
                              includeArray: [...(prev.includeArray || []), prev.includeValue],
                              includeValue: "", // Clear the input box after adding
                            }));
                          }
                        }}
                      >
                        Add
                      </button>
                    </div>
                    <ul className="list-group mt-3">
                      {isBilling.includeArray?.map((input, index) => (
                        <li
                          key={index}
                          className="list-group-item d-flex justify-content-between align-items-center"
                        >
                          {input}
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() =>
                              setIsBilling((prev) => ({
                                ...prev,
                                includeArray: prev.includeArray.filter((_, i) => i !== index),
                              }))
                            }
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>}

                  {/* Second Input Field */}
                  {!isBilling.selection && <div className="mb-3">
                    <label className="form-label">What not to Include</label>
                    <div className="d-flex align-items-center">
                      <input
                        type="text"
                        value={isBilling.notIncludeValue || ""}
                        onChange={(e) =>
                          setIsBilling((prev) => ({
                            ...prev,
                            notIncludeValue: e.target.value,
                          }))
                        }
                        className="form-control me-2"
                        placeholder="Enter a string"
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          if (isBilling.notIncludeValue?.trim()) {
                            setIsBilling((prev) => ({
                              ...prev,
                              notIncludeArray: [...(prev.notIncludeArray || []), prev.notIncludeValue],
                              notIncludeValue: "", // Clear the input box after adding
                            }));
                          }
                        }}
                      >
                        Add
                      </button>
                    </div>
                    <ul className="list-group mt-3">
                      {isBilling.notIncludeArray?.map((input, index) => (
                        <li
                          key={index}
                          className="list-group-item d-flex justify-content-between align-items-center"
                        >
                          {input}
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() =>
                              setIsBilling((prev) => ({
                                ...prev,
                                notIncludeArray: prev.notIncludeArray.filter((_, i) => i !== index),
                              }))
                            }
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>}
                  <div className="d-flex gap-2 mt-2">
                    {suggestion.map((input, index) => (
                      ![...isBilling.notIncludeArray, ...isBilling.includeArray].includes(input) && ( // Only show if not already in the array
                        <span
                          key={index}
                          className="badge bg-primary align-items-center"
                          style={{ padding: "10px", borderRadius: "20px", cursor: "pointer" }}
                          onClick={() => {
                            if (isBilling.selection) {
                              setIsBilling((prev) => ({
                                ...prev,
                                includeArray: [...(prev.includeArray || []), input],
                              }))
                            } else {
                              setIsBilling((prev) => ({
                                ...prev,
                                notIncludeArray: [...(prev.notIncludeArray || []), input],
                              }))
                            }
                          }
                          }
                        >
                          {input}
                          <span className="ms-2">
                            <i className="fa fa-plus-circle" style={{ fontSize: "1rem" }}></i>
                          </span>
                        </span>
                      )
                    ))}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setIsBilling({
                      _id: null,
                      includeArray: [],
                      includeValue: "",
                      notIncludeValue: "",
                      notIncludeArray: []
                    })}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ backgroundColor: '#1edd60', border: "none" }}
                    onClick={handleUpdateBilling}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {editMember && (
        <div className="modal show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Member</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setEditMember(null)}
                ></button>
              </div>
              <div className="modal-body">
                <form>
                  <div className="row g-3">
                    <div className="col-lg-6">
                      <label for="name" className="form-label">Name</label>
                      <input type="text" className="form-control" id="name" placeholder="Enter your name"
                        value={editMember.name}
                        onChange={(e) =>
                          setEditMember({ ...editMember, name: e.target.value })
                        } />
                      <span className="text-danger small">{validator.message("Name", editMember.name, "required|max:50", { max: "Name cannot exceed 50 characters." })}</span>
                    </div>
                    <div className="col-lg-6">
                      <label for="designation" className="form-label">Designation</label>
                      <input type="text" className="form-control" id="designation"
                        placeholder="Enter your designation"
                        value={editMember.designation}
                        onChange={(e) =>
                          setEditMember({ ...editMember, designation: e.target.value })
                        } />
                      <span className="text-danger small">{validator.message("Designation", editMember.designation, "required|max:50", { max: "Designation cannot exceed 50 characters." })}</span>
                    </div>
                    <div className="col-lg-6">
                      <label for="mobile" className="form-label">Mobile Number</label>
                      <input type="number" className="form-control" id="mobile"
                        placeholder="Enter your mobile number"
                        value={editMember.userPhone}
                        onChange={(e) => {
                          if (e.target.value?.length > 10 || isNaN(e.target.value)) {
                            return
                          }
                          setEditMember({ ...editMember, userPhone: e.target.value })
                        }}
                      />
                      <span className="text-danger small">{validator.message("Phone Number", editMember.userPhone, "required|regex:^\\d{10}$", { regex: "Enter a valid 10-digit phone number." })}</span>
                    </div>
                    <div className="col-lg-6">
                      <label for="username" className="form-label">Username</label>
                      <input type="text" className="form-control" id="username"
                        placeholder="Enter your username"
                        value={editMember.username}
                        onChange={(e) =>
                          setEditMember({ ...editMember, username: e.target.value })
                        } />
                      <span className="text-danger small">{validator.message("username", editMember.username, "required|max:50", { max: "username cannot exceed 50 characters." })}</span>
                    </div>
                    <div className="col-lg-6">
                      <label for="email" className="form-label">Email</label>
                      <input type="email" className="form-control" id="email"
                        placeholder="Enter your email"
                        value={editMember.userEmail}
                        onChange={(e) =>
                          setEditMember({ ...editMember, userEmail: e.target.value })
                        } />
                      <span className="text-danger small">{validator.message("userEmail", editMember.userEmail, "required|email|max:50", { max: "email cannot exceed 50 characters." })}</span>
                    </div>
                    <div className="col-lg-6 mb-3">
                      <label htmlFor="gender" className="form-label">Gender</label>
                      <div className="dropdown">
                        <button
                          className="btn btn-sm btn-outline-secondary dropdown-toggle w-100"
                          type="button"
                          id="genderDropdown"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          {editMember.gender
                            ? editMember.gender.charAt(0).toUpperCase() + editMember.gender.slice(1)
                            : "Select Gender"}
                        </button>

                        <ul className="dropdown-menu w-100" aria-labelledby="genderDropdown">
                          <li>
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() => setEditMember({ ...editMember, gender: "" })}
                            >
                              Select Gender
                            </button>
                          </li>
                          <li>
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() => setEditMember({ ...editMember, gender: "male" })}
                            >
                              Male
                            </button>
                          </li>
                          <li>
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() => setEditMember({ ...editMember, gender: "female" })}
                            >
                              Female
                            </button>
                          </li>
                        </ul>
                      </div>
                      <span className="text-danger small">
                        {validator.message("gender", editMember.gender, "required")}
                      </span>
                    </div>

                    <div className="col-lg-6">
                      <label className="form-label">Select Departments</label>
                      <DropdownWithCheckbox options={departments} selectedItems={editMember?.departments || []} setSelectedItems={(data) => { setEditMember({ ...editMember, "departments": data }) }} />
                      <span className="text-danger small">{validator.message("departments", editMember.departments, "required")}</span>
                    </div>
                    <div className="col-lg-12 d-flex align-items-center">
                      <label className="form-label me-3">Update Password:</label>
                      <div className="form-check form-switch">
                        <input className="form-check-input" type="checkbox" id="updatePasswordToggle"
                          checked={editMember.isPasswordUpdate}
                          onChange={(e) =>
                            setEditMember({
                              ...editMember,
                              isPasswordUpdate: e.target.checked,
                              userPassword: e.target.checked ? "" : users.find(x => x._id == editMember._id).userPassword,
                            })
                          } />
                      </div>
                      <small className="ms-3">*If you want to edit the password, toggle the switch and
                        make changes</small>
                    </div>
                    {editMember.isPasswordUpdate && <div className="col-lg-12" id="passwordField">
                      <label for="password" className="form-label">Password</label>
                      <input type="password" className="form-control" id="password"
                        placeholder="Enter new password"
                        value={editMember.userPassword}
                        onChange={(e) =>
                          setEditMember({
                            ...editMember,
                            userPassword: e.target.value,
                          })
                        } />
                      <span className="text-danger small">
                        {validator.message(
                          "password",
                          editMember.userPassword,
                          "required|passwordValidation"
                        )}
                      </span>
                    </div>}
                    <div className="col-lg-12">
                      <label for="profileImage" className="form-label">Profile Image</label>
                      <input type="file" className="form-control" id="profileImage" accept="image/*"
                        onChange={(e) => { handleFileChange(e, true) }} />
                      <span className="text-danger small">{validator.message("Profile Image", editMember.profileImage, "required")}</span>
                    </div>
                    {editMember?.profileImage && (
                      <div style={{ width: '100%', height: '200px' }}>
                        <Cropper
                          ref={cropperRef}
                          src={!editMember.isImgUpdated ? CONFIG.REACT_APP_UPLOADS + "/" + editMember.profileImage : editMember.profileImage}
                          aspectRatio={1}
                          style={{ width: '100%', height: '100%' }}
                          initialAspectRatio={1}
                          viewMode={1}
                          crop={onCrop}
                          minCropBoxWidth={100}
                          minCropBoxHeight={100}
                          background={false}
                          responsive={true}
                          autoCropArea={1}
                          checkOrientation={false}
                          guides={true}
                        />
                      </div>
                    )}
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditMember(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ backgroundColor: '#1edd60', border: "none" }}
                  onClick={handleUpdateMember}
                >
                  Update Member
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Users
